import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  FileText, Shield, CheckCircle2, Users, 
  Target, Eye, Lock, Crown, Building2
} from "lucide-react";
import { TrustNavigation } from "@/components/TrustNavigation";
import { TrustFooter } from "@/components/TrustFooter";

export default function Deliverables() {

  const dossierSections = [
    {
      title: "Candidate Summary",
      description: "High-level overview including role fit assessment, key strengths, and agent recommendation.",
      allTiers: true
    },
    {
      title: "Assessment Performance",
      description: "Summary of video assessment responses with quality indicators and consistency notes.",
      allTiers: true
    },
    {
      title: "Credential Verification",
      description: "Status of verified qualifications, certifications, and relevant credentials.",
      allTiers: true
    },
    {
      title: "Experience Assessment",
      description: "Evaluation of claimed experience depth based on structured assessment responses.",
      allTiers: true
    },
    {
      title: "Candidate Location",
      description: "General location information for logistics planning.",
      allTiers: true
    },
    {
      title: "Contact Information",
      description: "Direct contact details (email, phone) for candidate outreach.",
      enterpriseOnly: true
    },
    {
      title: "Full Resume/CV",
      description: "Complete candidate-submitted resume document.",
      enterpriseOnly: true
    },
    {
      title: "Video Recordings",
      description: "Access to full assessment video recordings.",
      enterpriseOnly: true
    }
  ];

  const tiers = [
    {
      name: "Core RPO",
      description: "Agent-managed service with curated dossier delivery",
      features: [
        "Agent-curated dossiers via secure share links",
        "Candidate summary and assessment",
        "Credential verification status",
        "Agent coordination for next steps"
      ],
      excluded: ["Dashboard access", "Direct contact details", "Full CV access"]
    },
    {
      name: "Professional RPO",
      description: "Dashboard access with curated candidate visibility",
      features: [
        "Dashboard access to published candidates",
        "Candidate summary and location",
        "Assessment performance summaries",
        "Agent-managed candidate pipeline"
      ],
      excluded: ["Direct contact details", "Full CV access", "Video recordings"]
    },
    {
      name: "Enterprise RPO",
      description: "Full pipeline visibility with complete candidate data",
      features: [
        "Full dashboard access",
        "Complete contact information",
        "Full resume/CV access",
        "Video recording access",
        "Direct candidate communication"
      ],
      excluded: []
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TrustNavigation currentPath="/deliverables" />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 py-10 md:py-24 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium border border-white/20 mb-6">
              <FileText className="w-4 h-4" />
              <span>Deliverables</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              What You Receive
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Detailed specification of candidate dossier contents and delivery formats across service tiers.
            </p>
            <p className="text-sm text-slate-400 mt-4">
              Last updated: January 2026 | Version 2.2
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Candidate Dossier Contents</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {dossierSections.map((section, idx) => (
                <Card key={idx} className={`p-5 shadow-sm ${section.enterpriseOnly ? 'border-amber-300 bg-amber-50' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{section.title}</h3>
                      <p className="text-sm text-slate-600">{section.description}</p>
                    </div>
                    {section.enterpriseOnly && (
                      <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded-full whitespace-nowrap font-medium">
                        Enterprise Only
                      </span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Deliverables by Tier</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {tiers.map((tier, idx) => (
                <Card key={idx} className="p-6 bg-white border-slate-200 shadow-sm">
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      {idx === 2 ? <Crown className="w-5 h-5 text-amber-500" /> : <Building2 className="w-5 h-5 text-slate-500" />}
                      <h3 className="font-bold text-slate-900">{tier.name}</h3>
                    </div>
                    <p className="text-sm text-slate-600">{tier.description}</p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Included</p>
                      <ul className="space-y-2">
                        {tier.features.map((feature, fIdx) => (
                          <li key={fIdx} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {tier.excluded.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-2">Not Included</p>
                        <ul className="space-y-1">
                          {tier.excluded.map((item, eIdx) => (
                            <li key={eIdx} className="flex items-start gap-2 text-sm text-slate-400">
                              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
